import { Injectable, OnDestroy } from '@angular/core';
import { Subscription, combineLatest } from 'rxjs';

import { ApplicationInsights, IExceptionTelemetry } from '@microsoft/applicationinsights-web';

import { UserDto, UserSettingDto } from 'src/app/api';
import { CerSessionService } from 'src/cer/cer-session/cer-session.service';
import { CerAppRouteService, } from 'src/cer-app/cer-app-route/cer-app-route.service';

@Injectable({
  providedIn: 'root',
})
export class CerAppApplicationInsightsService implements OnDestroy {
  private subscriptionManager$ = new Subscription();
  private appInsights: ApplicationInsights = null;

  private applicationName: string = '';
  private userId: number = null;
  private userName: string = ''

  constructor(private routeService: CerAppRouteService, private session: CerSessionService) {

    this.subscriptionManager$.add(
      combineLatest([session.user.user$,session.settings$]).subscribe(([user,settings]) => this.setupAppInsightsFromUserSettings(user,settings))
    );

    this.subscriptionManager$.add(
      this.session.environment.title$.subscribe(applicationName => this.applicationName = applicationName)
    );
  }

  private subscribeActiveUserRoute() {
    this.subscriptionManager$.add(
      this.routeService.activeUserRoute$.subscribe(route => {
        if (route) {
          this.logPageView(route.routeUrl, route.routeUrl);
        }
      })
    );
  }

  public ngOnDestroy(): void {
    this.subscriptionManager$.unsubscribe();
  }

  private setupAppInsightsFromUserSettings(user: UserDto, settings: UserSettingDto) {
    if (user != null) {
      this.userId = user?.id;
      this.userName = user?.shortName;

      if (!this.appInsights) {
        if (settings) {
          const connStr: string = settings.settings['AppInsightsConnectionString'];
          if (connStr && connStr.length > 0) {
            this.startAppInsights(connStr);
          }
        }
      }
    }
  }

  private startAppInsights(connectionString: string) {

    this.appInsights = new ApplicationInsights({
      config: {
        connectionString: connectionString,
        enableCorsCorrelation: true
      }
    });

    this.appInsights.loadAppInsights();
    this.appInsights.setAuthenticatedUserContext(this.userId.toString());
    this.appInsights.addTelemetryInitializer((envelope : any) => {
      var item = envelope.baseData;
      item.properties = item.properties || {};
      item.properties["ApplicationPlatform"] = "Browser";
      item.properties["ApplicationName"] = this.applicationName;
      item.properties["UserId"] = this.userId;
      item.properties["UserName"] = this.userName;
    });
    this.subscribeActiveUserRoute();
  }

  private clearUserId() {
    if (this.appInsights) {
      this.appInsights.clearAuthenticatedUserContext();
    }
  }

  logPageView(name?: string, uri?: string) {
    if (this.appInsights) {
      this.appInsights.trackPageView({ name, uri });
    }
    else {
      console.log((uri ?? '') + ' ' + (name ?? ''));
    }
  }

  logException(error: Error) {
    if (error?.message?.includes("no resource to load, early way out") ||
        error?.message?.includes("ResizeObserver loop limit exceeded")) {
      return;
    }
    if (this.appInsights) {
      let exception: IExceptionTelemetry = {
        exception: error
      };
      this.appInsights.trackException(exception);
    }
    else {
      console.error(error);
    }
  }

  logTrace(message: string, properties?: { [key: string]: any }) {
    if (this.appInsights) {
      this.appInsights.trackTrace({ message: message }, properties);
    }
    else {
      console.log(message);
    }
  }
}
